<script lang="ts">
    import type { Lead, Field } from '@/types/types';
    import type Blocks from '@/schemas/public/Blocks';
    import type { DependencyMap } from '@/types/fieldDependency';
    import KanbanBoard from './KanbanBoard.svelte';
    import StatusChangeModal from './StatusChangeModal.svelte';
    import DeleteConfirmModal from './DeleteConfirmModal.svelte';
    import LeadDetailModal from './LeadDetailModal.svelte';
    import LeadKanbanFilterBar from './LeadKanbanFilterBar.svelte';
    import LeadTableView from './LeadTableView.svelte';
    import LeadCalendarView from './LeadCalendarView.svelte';
    import * as ToggleGroup from '@/components/ui/toggle-group/index.js';
    import { Button } from '@/components/ui/button';
    import { router, useForm } from '@inertiajs/svelte';
    import { LayoutGrid, Table as TableIcon, CalendarDays, Plus } from 'lucide-svelte';
    import { toast } from 'svelte-sonner';
    import axios from 'axios';
    import { createDefaultFilters, hasActiveFilters, countActiveFilters, filtersToApiParams, type LeadFilters } from './kanban-filters.svelte';
    import { statusesThatRequireJob } from '@/lib/leadStatusRules';

    // ── Types ────────────────────────────────────────────────────────────

    type SortType = 'updated_at' | 'estimated_job_start' | 'created_at';

    type StatusOption = {
        key: string;
        label: string;
        color: string;
        cssClass: string;
    };

    // ── Props ────────────────────────────────────────────────────────────

    type Props = {
        leads?: Lead[];
        blocks: (Blocks & { fields: Field[] })[];
        documentBlocks: (Blocks & { fields: Field[] })[];
        siteSurveyBlocks: (Blocks & { fields: Field[] })[];
        siteSurveyDependencyMap?: DependencyMap;
        statusOptions?: StatusOption[];
        onAddNew?: () => void;
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let { leads = [], blocks, documentBlocks, siteSurveyBlocks, siteSurveyDependencyMap = {}, statusOptions = [], onAddNew }: Props = $props();

    const statuses = $derived(statusOptions);
    const kanbanStatuses = $derived(statuses.filter((s) => s.key !== 'archived'));

    // ── View toggle (persisted in sessionStorage) ────────────────────────

    type ViewType = 'kanban' | 'table' | 'calendar';
    const VIEW_STORAGE_KEY = 'leads-view';
    const validViews: ViewType[] = ['kanban', 'table', 'calendar'];

    function getInitialView(): ViewType {
        if (typeof window === 'undefined') return 'kanban';
        const stored = sessionStorage.getItem(VIEW_STORAGE_KEY);
        return stored && validViews.includes(stored as ViewType) ? (stored as ViewType) : 'kanban';
    }

    let currentView = $state<ViewType>(getInitialView());

    $effect(() => {
        sessionStorage.setItem(VIEW_STORAGE_KEY, currentView);
    });

    let headerBarHeight = $state(0);

    // ── Visibility reload ────────────────────────────────────────────────

    $effect(() => {
        function handleVisibilityChange() {
            if (!document.hidden) {
                if (hasActiveFilters(filters)) {
                    fetchFilteredLeads();
                } else {
                    router.reload({ only: ['leads'] });
                }
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    });

    // ── Kanban filter & sort state ───────────────────────────────────────

    let filters = $state<LeadFilters>(createDefaultFilters());
    let isFiltering = $state(false);
    let filteredLeads = $state<Lead[]>([]);
    let filterTimeout: ReturnType<typeof setTimeout> | null = null;
    const DEBOUNCE_MS = 350;
    let sortBy = $state<SortType>('updated_at');

    let useFiltered = $derived(hasActiveFilters(filters));
    let displayLeads = $derived(useFiltered ? filteredLeads : leads);
    let activeFilterCount = $derived(countActiveFilters(filters));

    async function fetchFilteredLeads() {
        isFiltering = true;
        try {
            const params = filtersToApiParams(filters);
            const res = await axios.get(route('web-api.Lead.index'), {
                params: { limit: 100, offset: 0, filters: params },
            });
            filteredLeads = res.data.rows;
        } catch (error) {
            console.error('Failed to fetch filtered leads:', error);
            toast.error('Failed to filter leads');
        } finally {
            isFiltering = false;
        }
    }

    function handleFilterChange() {
        if (filterTimeout) clearTimeout(filterTimeout);
        filterTimeout = setTimeout(() => {
            if (hasActiveFilters(filters)) fetchFilteredLeads();
        }, DEBOUNCE_MS);
    }

    function handleClearFilters() {
        filters = createDefaultFilters();
        filteredLeads = [];
    }

    // ── Lead sorting & grouping ──────────────────────────────────────────

    function sortLeads(leadsToSort: Lead[]): Lead[] {
        return [...leadsToSort].sort((a, b) => {
            const key = sortBy === 'estimated_job_start' ? 'estimated_job_start' : sortBy === 'created_at' ? 'created_at' : 'updated_at';
            const desc = key !== 'estimated_job_start';
            const aVal = a[key] ? new Date(a[key] as string).getTime() : null;
            const bVal = b[key] ? new Date(b[key] as string).getTime() : null;
            if (!aVal && !bVal) return 0;
            if (!aVal) return 1;
            if (!bVal) return -1;
            return desc ? bVal - aVal : aVal - bVal;
        });
    }

    let leadsByStatus = $derived(
        statuses.reduce(
            (acc, status) => {
                acc[status.key] = sortLeads(displayLeads.filter((lead) => lead.status === status.key));
                return acc;
            },
            {} as Record<string, Lead[]>,
        ),
    );

    // ── Modal state ──────────────────────────────────────────────────────

    let showStatusChangeModal = $state(false);
    let showDeleteModal = $state(false);
    let showDetailModal = $state(false);
    let selectedLeadId = $state<string | null>(null);
    let leadToDelete = $state<Lead | null>(null);
    let detailLeadData = $state<Record<string, unknown> | null>(null);
    let isLoadingDetail = $state(false);
    let isLoadingStatusChange = $state(false);
    // Holds the fully-hydrated lead (with site_survey + lineItems) fetched on demand
    // before opening StatusChangeModal. Cards in the kanban payload no longer carry
    // those heavy fields, so we hydrate just-in-time.
    let hydratedLead = $state<Lead | null>(null);
    let targetStatus = $state<string>('');
    let statusChangeForm = $state(useForm({}));

    $effect(() => {
        if (!showDetailModal) detailLeadData = null;
    });

    $effect(() => {
        if (!showStatusChangeModal) hydratedLead = null;
    });

    let selectedLead = $derived.by(() => {
        if (!selectedLeadId) return null;
        if (hydratedLead && hydratedLead.id === selectedLeadId) return hydratedLead;
        return leads.find((l) => l.id === selectedLeadId) || null;
    });

    async function fetchLeadAndOpenStatusModal(lead: Lead, newStatus: string) {
        selectedLeadId = lead.id;
        targetStatus = newStatus;
        isLoadingStatusChange = true;
        try {
            const response = await fetch(`/web-api/leads/${lead.id}/details`, {
                method: 'GET',
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!response.ok) throw new Error('Failed to fetch lead details');
            const data = await response.json();
            hydratedLead = data.lead as Lead;
        } catch (error) {
            console.error('Error fetching lead details:', error);
            toast.error('Failed to load lead details');
            selectedLeadId = null;
            targetStatus = '';
            return;
        } finally {
            isLoadingStatusChange = false;
        }
        showStatusChangeModal = true;
    }

    // ── Kanban drop handlers ─────────────────────────────────────────────

    function handleColumnDrop(leadId: string, newStatus: string) {
        const lead = leads.find((l) => l.id === leadId);
        if (!lead || lead.status === newStatus) return;

        if (statusesThatRequireJob.includes(newStatus) && !lead.converted_to_job_id) {
            toast.error('This lead must have a converted job before moving to this status.');
            return;
        }

        fetchLeadAndOpenStatusModal(lead, newStatus);
    }

    function handleDeleteDrop(leadId: string) {
        const lead = leads.find((l) => l.id === leadId);
        if (!lead) return;
        leadToDelete = lead;
        showDeleteModal = true;
    }

    function handleArchiveDrop(leadId: string) {
        const lead = leads.find((l) => l.id === leadId);
        if (!lead || lead.status === 'archived') return;
        fetchLeadAndOpenStatusModal(lead, 'archived');
    }

    // ── Status change ────────────────────────────────────────────────────

    function handleStatusChange(leadId: string, newStatus: string, data: Record<string, unknown>) {
        statusChangeForm = useForm(data);
        // No `only:` here — partial reloads with `only` strip server-flashed
        // validation errors out of the response props before they reach
        // onError, leaving the user with a silent no-op when (for example)
        // LeadUpdateRequest rejects a booked transition for incomplete phases.
        // The full reload is fine for a status change frequency-wise.
        $statusChangeForm.put(`/leads/${leadId}`, {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                showStatusChangeModal = false;
                selectedLeadId = null;
                targetStatus = '';
                statusChangeForm = useForm({});
            },
            onError: (errors) => {
                // LeadUpdateRequest::withValidator flashes status-transition
                // rejections under keys like `phases`, `site_survey`, `status`,
                // and `message`. The modal only renders per-field errors that
                // match a column_name, so non-field errors were previously
                // invisible — the user saw a silent no-op. Surface anything
                // that's not already wired to an inline field as a toast.
                const flat = errors as Record<string, string>;
                const broadKeys = ['message', 'phases', 'site_survey', 'status'];
                for (const key of broadKeys) {
                    if (flat[key]) {
                        toast.error(flat[key]);
                    }
                }
            },
        });
    }

    function handleCancelStatusChange() {
        showStatusChangeModal = false;
        selectedLeadId = null;
        targetStatus = '';
        statusChangeForm = useForm({});
    }

    // ── Detail modal ─────────────────────────────────────────────────────

    async function handleCardClick(lead: Lead) {
        isLoadingDetail = true;
        showDetailModal = true;
        try {
            const response = await fetch(`/web-api/leads/${lead.id}/details`, {
                method: 'GET',
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!response.ok) throw new Error('Failed to fetch lead details');
            detailLeadData = await response.json();
        } catch (error) {
            console.error('Error fetching lead details:', error);
            toast.error('Failed to load lead details');
            showDetailModal = false;
        } finally {
            isLoadingDetail = false;
        }
    }

    function handleLeadUpdated(updatedLead: Lead) {
        if (detailLeadData) {
            detailLeadData = { ...detailLeadData, lead: updatedLead };
        }
        const idx = leads.findIndex((l) => l.id === updatedLead.id);
        if (idx !== -1) {
            leads = [...leads.slice(0, idx), updatedLead, ...leads.slice(idx + 1)];
        }
    }

    function handleCloseDeleteModal() {
        showDeleteModal = false;
        leadToDelete = null;
    }
</script>

<!-- ── Layout ──────────────────────────────────────────────────────────── -->

<div class="leads-wrapper" class:page-scroll={currentView === 'table' || currentView === 'calendar'}>
    <div class="header-bar" bind:clientHeight={headerBarHeight}>
        {#if currentView === 'kanban'}
            <LeadKanbanFilterBar
                bind:filters
                isLoading={isFiltering}
                {activeFilterCount}
                {sortBy}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onSortChange={(sort) => (sortBy = sort)}
            />
        {:else}
            {#if onAddNew}
                <Button size="sm" onclick={onAddNew} class="flex items-center gap-2">
                    <Plus size={16} />
                    Add New Lead
                </Button>
            {/if}
            <div class="header-spacer"></div>
        {/if}

        <ToggleGroup.Root bind:value={currentView} type="single" variant="outline" size="sm" class="view-toggle">
            <ToggleGroup.Item value="kanban" aria-label="Kanban view" class="flex items-center gap-2">
                <LayoutGrid size={16} />
                Kanban
            </ToggleGroup.Item>
            <ToggleGroup.Item value="table" aria-label="Table view" class="flex items-center gap-2">
                <TableIcon size={16} />
                Table
            </ToggleGroup.Item>
            <ToggleGroup.Item value="calendar" aria-label="Calendar view" class="flex items-center gap-2 px-3">
                <CalendarDays size={16} />
                Calendar
            </ToggleGroup.Item>
        </ToggleGroup.Root>
    </div>

    {#if currentView === 'kanban'}
        <KanbanBoard
            {leadsByStatus}
            statuses={kanbanStatuses}
            onColumnDrop={handleColumnDrop}
            onDeleteDrop={handleDeleteDrop}
            onArchiveDrop={handleArchiveDrop}
            onCardClick={handleCardClick}
            {onAddNew}
        />
    {:else if currentView === 'table'}
        <LeadTableView onRowClick={handleCardClick} statusOptions={statuses} />
    {:else if currentView === 'calendar'}
        <LeadCalendarView {statuses} stickyTopOffset={headerBarHeight} />
    {/if}
</div>

<!-- ── Modals ──────────────────────────────────────────────────────────── -->

{#if showStatusChangeModal && selectedLead}
    <StatusChangeModal
        lead={selectedLead}
        newStatus={targetStatus}
        statusLabel={statuses.find((s) => s.key === targetStatus)?.label ?? targetStatus}
        {blocks}
        {siteSurveyBlocks}
        {siteSurveyDependencyMap}
        errors={$statusChangeForm.errors}
        processing={$statusChangeForm.processing}
        onConfirm={(data) => handleStatusChange(selectedLead.id, targetStatus, data)}
        onCancel={handleCancelStatusChange}
        bind:show={showStatusChangeModal}
    />
{/if}

<DeleteConfirmModal bind:open={showDeleteModal} lead={leadToDelete} onClose={handleCloseDeleteModal} />

{#if showDetailModal && detailLeadData}
    {#key (detailLeadData as Record<string, any>).lead.id}
        <LeadDetailModal
            bind:show={showDetailModal}
            lead={(detailLeadData as Record<string, any>).lead}
            blocks={(detailLeadData as Record<string, any>).blocks}
            documentBlocks={(detailLeadData as Record<string, any>).documentBlocks}
            siteSurveyBlocks={(detailLeadData as Record<string, any>).siteSurveyBlocks || siteSurveyBlocks}
            siteSurveyDependencyMap={(detailLeadData as Record<string, any>).siteSurveyDependencyMap || siteSurveyDependencyMap}
            siteVisitBlocks={(detailLeadData as Record<string, any>).siteVisitBlocks}
            relatedDocuments={(detailLeadData as Record<string, any>).relatedDocuments}
            siteSurveyImages={(detailLeadData as Record<string, any>).siteSurveyImages}
            siteVisits={(detailLeadData as Record<string, any>).siteVisits}
            onLeadUpdated={handleLeadUpdated}
        />
    {/key}
{:else if showDetailModal && isLoadingDetail}
    <div class="loading-overlay">
        <div class="loading-spinner">Loading lead details...</div>
    </div>
{/if}

<style lang="scss">
    .leads-wrapper {
        display: flex;
        flex-direction: column;
        height: calc(100vh - 100px);
        padding: 0 1rem;

        &.page-scroll {
            height: auto;
            overflow: visible;
        }
    }

    .header-bar {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.5rem 0;

        .page-scroll & {
            position: sticky;
            top: var(--header-height, 0px);
            z-index: 40;
            background: var(--background);
        }
    }

    .header-spacer {
        flex: 1;
    }

    :global(.view-toggle) {
        flex-shrink: 0;
        margin-left: auto;
    }

    .loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .loading-spinner {
        background: var(--clr-surface-1);
        padding: 2rem;
        border-radius: 8px;
        font-size: 1rem;
        color: var(--clr-on-surface-1);
    }
</style>